import { Order as OrderType, Item } from '@baemingo/order-status-events/dist/types';
import { useMemo } from 'react';
import { useOrderStatusUniverse } from '~Components/OrderStatusUniverse';
import { useStorage } from '~Components/Storage';
import { Station } from '~Types';
import { useLogger } from '~Hooks';

interface Props {
  station?: Station | null;
}

const useFilterOrders = ({ station }: Props) => {
  const [storage] = useStorage();
  const universe = useOrderStatusUniverse();

  const orders = useMemo(() => {
    return universe.orders.reduce((arr, order) => {
      let items = universe.items.filter((item) => item.orderId === order.id);
      let matchingServiceType = true;
      let matchingOrigin = true;

      if (station) {
        items = items.filter((item) => {
          // If we're showing all order statuses, we don't need to filter on item status else
          // Check if the statuses we've set for this station is a match
          const orderStatusMatch =
            storage.KDSShowAllOrderStatuses ||
            (station.orderStatuses.length > 0
              ? station.orderStatuses.includes(item.status)
              : true);

          const noFilterMatch = station.items.length === 0 && station.categories.length === 0;

          // Check if item ID or category ID/name is part of this station
          const itemMatch = station.items.some((id) => id === item.itemId);
          const categoryMatch = station.categories.some(
            (category) => category.name === item.categoryId || category.id === item.categoryId,
          );

          return (
            orderStatusMatch &&
            (noFilterMatch ||
              itemMatch ||
              categoryMatch ||
              (!!order.realId && !storage.enableNewKDSAppOrders))
          );
        });

        // Filter station origins/service types
        matchingServiceType =
          order.serviceType && station.serviceTypes.length > 0
            ? station.serviceTypes.includes(order.serviceType)
            : true;

        matchingOrigin =
          order.origin && station.origins.length > 0
            ? station.origins.includes(order.origin)
            : true;
      }

      const matchingOrderStatus =
        storage.KDSFilterOnWholeOrderStatus && station
          ? station.orderStatuses.includes(order.status)
          : true;

      // Only add this order if any of its items are relevant for the station and matches filters
      if (items.length > 0 && matchingServiceType && matchingOrigin && matchingOrderStatus) {
        const withItems = {
          ...order,
          items,
        };

        arr.push(withItems);
      }

      return arr;
    }, [] as Array<{ items: Item[] } & OrderType>);
  }, [universe, station]);

  return orders;
};

export default useFilterOrders;
